import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import './LoveTreePage.css';

const LoveTreePage = () => {
  const [gameStarted, setGameStarted] = useState(false);
  const [showSoil, setShowSoil] = useState(false);
  const [showTrunk, setShowTrunk] = useState(false);
  const [showLeaves, setShowLeaves] = useState(false);
  const [showPoem, setShowPoem] = useState(false);
  const [showDays, setShowDays] = useState(false);
  const [leaves, setLeaves] = useState([]);

  // 计算在一起的天数（假设从2023年2月14日开始）
  const startDate = new Date('2023-02-14');
  const currentDate = new Date();
  const daysTogether = Math.floor((currentDate - startDate) / (1000 * 60 * 60 * 24));

  // 生成沿着树枝分布并汇聚成爱心的叶子
  const generateLeaves = () => {
    const colors = ['yellow', 'red', 'orange', 'pink', 'green'];
    const newLeaves = [];
    
    // 定义树枝的路径点（基于SVG中的路径）
    const branchPaths = [
      // 主树干顶部区域
      {path: [{x: 0, y: -120}, {x: 0, y: -100}, {x: 0, y: -80}], density: 8},
      
      // 左侧主分支：M 150 140 Q 120 120 90 110 (转换为相对坐标)
      {path: [{x: 0, y: -60}, {x: -30, y: -80}, {x: -60, y: -90}], density: 12},
      
      // 右侧主分支：M 150 140 Q 180 120 210 110
      {path: [{x: 0, y: -60}, {x: 30, y: -80}, {x: 60, y: -90}], density: 12},
      
      // 左上分支：M 150 100 Q 110 85 80 75
      {path: [{x: 0, y: -100}, {x: -40, y: -115}, {x: -70, y: -125}], density: 10},
      
      // 右上分支：M 150 100 Q 190 85 220 75
      {path: [{x: 0, y: -100}, {x: 40, y: -115}, {x: 70, y: -125}], density: 10},
      
      // 左侧次级分支
      {path: [{x: -60, y: -90}, {x: -90, y: -100}, {x: -120, y: -115}], density: 8},
      
      // 右侧次级分支
      {path: [{x: 60, y: -90}, {x: 90, y: -100}, {x: 120, y: -115}], density: 8},
      
      // 左上次级分支
      {path: [{x: -70, y: -125}, {x: -85, y: -135}, {x: -100, y: -145}], density: 6},
      
      // 右上次级分支
      {path: [{x: 70, y: -125}, {x: 85, y: -135}, {x: 100, y: -145}], density: 6}
    ];
    
    let leafId = 0;
    
    // 在每个树枝路径上生成叶子
    branchPaths.forEach((branch, branchIndex) => {
      for (let i = 0; i < branch.density; i++) {
        // 沿着分支路径分布叶子
        const progress = i / (branch.density - 1);
        let x, y;
        
        if (branch.path.length === 3) {
          // 贝塞尔曲线插值
          const t = progress;
          const p0 = branch.path[0];
          const p1 = branch.path[1];
          const p2 = branch.path[2];
          
          x = Math.pow(1-t, 2) * p0.x + 2 * (1-t) * t * p1.x + Math.pow(t, 2) * p2.x;
          y = Math.pow(1-t, 2) * p0.y + 2 * (1-t) * t * p1.y + Math.pow(t, 2) * p2.y;
        } else {
          // 线性插值
          const startPoint = branch.path[0];
          const endPoint = branch.path[branch.path.length - 1];
          x = startPoint.x + (endPoint.x - startPoint.x) * progress;
          y = startPoint.y + (endPoint.y - startPoint.y) * progress;
        }
        
        // 添加随机偏移让叶子看起来更自然
        const offsetX = (Math.random() - 0.5) * 20;
        const offsetY = (Math.random() - 0.5) * 20;
        
        newLeaves.push({
          id: leafId++,
          x: x + offsetX,
          y: y + offsetY,
          color: colors[Math.floor(Math.random() * colors.length)],
          delay: Math.random() * 3 + branchIndex * 0.3, // 分支依次出现
          branchIndex: branchIndex
        });
      }
    });
    
    // 添加额外的叶子形成完整的爱心轮廓
    for (let i = 0; i < 40; i++) {
      const t = (i / 40) * 2 * Math.PI;
      
      // 爱心的参数方程
      const scale = 1.5;
      const heartX = scale * (16 * Math.pow(Math.sin(t), 3));
      const heartY = -scale * (13 * Math.cos(t) - 5 * Math.cos(2*t) - 2 * Math.cos(3*t) - Math.cos(4*t)) - 100;
      
      // 只添加距离树枝不太远的爱心轮廓点
      const distanceToTree = Math.sqrt(heartX * heartX + (heartY + 100) * (heartY + 100));
      if (distanceToTree < 150) {
        const offsetX = (Math.random() - 0.5) * 15;
        const offsetY = (Math.random() - 0.5) * 15;
        
        newLeaves.push({
          id: leafId++,
          x: heartX + offsetX,
          y: heartY + offsetY,
          color: colors[Math.floor(Math.random() * colors.length)],
          delay: Math.random() * 2 + 2, // 稍晚出现，形成爱心轮廓
          branchIndex: 999 // 特殊标记
        });
      }
    }
    
    // 添加一些内部填充叶子
    for (let i = 0; i < 30; i++) {
      const angle = Math.random() * 2 * Math.PI;
      const radius = Math.random() * 60;
      const x = radius * Math.cos(angle);
      const y = radius * Math.sin(angle) - 110;
      
      newLeaves.push({
        id: leafId++,
        x: x,
        y: y,
        color: colors[Math.floor(Math.random() * colors.length)],
        delay: Math.random() * 3 + 1,
        branchIndex: 1000 // 内部填充
      });
    }
    
    setLeaves(newLeaves);
  };

  const handleHeartClick = () => {
    if (gameStarted) return;
    
    setGameStarted(true);
    
    // 土壤立即出现（爱心变成红点后）
    setShowSoil(true);
    
    // 创建下落的红点
    const fallingDot = document.createElement('div');
    fallingDot.className = 'falling-dot';
    fallingDot.style.left = '50%';
    fallingDot.style.top = '50%';
    fallingDot.style.transform = 'translate(-50%, -50%)';
    document.querySelector('.lovetree-page').appendChild(fallingDot);
    
    // 移除下落的红点
    setTimeout(() => {
      if (fallingDot.parentNode) {
        fallingDot.parentNode.removeChild(fallingDot);
      }
    }, 1500);
    
    // 显示树干 - 红点落地后0.5秒开始
    setTimeout(() => setShowTrunk(true), 2000);
    
    // 生成并显示叶子 - 等树枝都画完
    setTimeout(() => {
      generateLeaves();
      setShowLeaves(true);
    }, 6000);
    
    // 显示诗句 - 6.5秒开始
    setTimeout(() => setShowPoem(true), 6500);
    
    // 显示天数 - 10.5秒开始
    setTimeout(() => setShowDays(true), 10500);
  };

  return (
    <div className="lovetree-page">
          <Link to="/" className="back-btn">
            <FaArrowLeft /> 返回首页
          </Link>

      {/* 中心爱心（初始状态） */}
      {!gameStarted && (
        <div className="heart-container">
          <div className="love-heart" onClick={handleHeartClick}></div>
          <div className="click-hint">Click Baby</div>
          </div>
      )}

      {/* 土壤线条 */}
      {showSoil && <div className="soil-line"></div>}

      {/* SVG树结构 */}
      {showTrunk && (
        <div className="tree-svg-container">
          <svg width="300" height="300" viewBox="0 0 300 300">
            {/* 渐变定义 */}
            <defs>
              <linearGradient id="trunkGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#8d6e63" />
                <stop offset="50%" stopColor="#6d4c41" />
                <stop offset="100%" stopColor="#5d4037" />
              </linearGradient>
              <linearGradient id="branchGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#8d6e63" />
                <stop offset="100%" stopColor="#6d4c41" />
              </linearGradient>
            </defs>
            
            {/* 主树干 - 稍微弯曲的路径 */}
            <path 
              className="main-trunk"
              d="M 150 280 Q 145 230 150 180 Q 155 130 150 80"
            />
            
            {/* 主要分支 */}
            <path 
              className="tree-branch branch-1"
              d="M 150 140 Q 120 120 90 110"
            />
            <path 
              className="tree-branch branch-2"
              d="M 150 140 Q 180 120 210 110"
            />
            <path 
              className="tree-branch branch-3"
              d="M 150 100 Q 110 85 80 75"
            />
            <path 
              className="tree-branch branch-4"
              d="M 150 100 Q 190 85 220 75"
            />
            
            {/* 次级分支 */}
            <path 
              className="tree-branch branch-1"
              d="M 90 110 Q 70 100 60 85"
              style={{animationDelay: '5s'}}
            />
            <path 
              className="tree-branch branch-2"
              d="M 210 110 Q 230 100 240 85"
              style={{animationDelay: '5.2s'}}
            />
            <path 
              className="tree-branch branch-3"
              d="M 80 75 Q 65 65 50 55"
              style={{animationDelay: '5.8s'}}
            />
            <path 
              className="tree-branch branch-4"
              d="M 220 75 Q 235 65 250 55"
              style={{animationDelay: '6.2s'}}
            />
          </svg>
          </div>
      )}

      {/* 树叶 */}
      {showLeaves && (
        <div className="tree-leaves">
          {leaves.map((leaf) => (
            <motion.div
              key={leaf.id}
              className={`leaf ${leaf.color}`}
              style={{
                transform: `translate(${leaf.x}px, ${leaf.y}px)`
              }}
              initial={{ opacity: 0, scale: 0, rotate: 0 }}
              animate={{ opacity: 1, scale: 1, rotate: 360 }}
              transition={{ 
                delay: 6.5 + leaf.delay, // 延迟到6.5秒后开始
                duration: 1.5,
                ease: "easeOut"
              }}
            />
                  ))}
                </div>
      )}

      {/* 左侧诗句 */}
      {showPoem && (
        <div className="love-poem">
          <motion.div 
            className="poem-line"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 6, duration: 2 }}
          >
            遇见你，就像跋山涉水遇见一轮月亮，
          </motion.div>
          <motion.div 
            className="poem-line"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 8, duration: 2 }}
          >
            以后天黑心伤，就问那天借一点月光。
          </motion.div>
            </div>
          )}

      {/* 天数统计 */}
      {showDays && (
        <motion.div 
          className="days-together"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 10, duration: 1 }}
        >
          <div>蛋蛋，我们已经在一起</div>
          <div><span className="days-number">{daysTogether}</span> 天啦</div>
        </motion.div>
      )}
      </div>
  );
};

export default LoveTreePage;